import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Grid, Chip, IconButton,
  Tabs, Tab, Divider, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BarChartIcon from '@mui/icons-material/BarChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

const API = '/api/contabilidad';
function fmt(v) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function CentrosCosto() {
  const [tab, setTab] = useState(0);
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [centros, setCentros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // CRUD Dialog
  const [dlgOpen, setDlgOpen] = useState(false);
  const [dlgEdit, setDlgEdit] = useState(null);
  const [form, setForm] = useState({ codigo: '', descripcion: '', activo: 1 });
  const [saving, setSaving] = useState(false);

  // Reporte
  const [periodos, setPeriodos] = useState([]);
  const [periodoDesde, setPeriodoDesde] = useState('');
  const [periodoHasta, setPeriodoHasta] = useState('');
  const [ccFiltro, setCcFiltro] = useState('');
  const [reporte, setReporte] = useState(null);
  const [loadingReporte, setLoadingReporte] = useState(false);

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) return;
    axios.get(`${API}/periodos?empresa_id=${empresaId}`).then(r => {
      setPeriodos(r.data);
      const abiertos = r.data.filter(p => p.estado === 'ABIERTO').sort((a,b) => b.periodo.localeCompare(a.periodo));
      if (abiertos.length > 0) {
        setPeriodoDesde(abiertos[abiertos.length - 1].periodo);
        setPeriodoHasta(abiertos[0].periodo);
      }
    }).catch(() => {});
    cargarCentros();
  }, [empresaId]);

  const cargarCentros = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/centros-costo?empresa_id=${empresaId}`);
      setCentros(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    setLoading(false);
  }, [empresaId]);

  function abrirNuevo() {
    setDlgEdit(null);
    setForm({ codigo: '', descripcion: '', activo: 1 });
    setDlgOpen(true);
  }

  function abrirEditar(cc) {
    setDlgEdit(cc);
    setForm({ codigo: cc.codigo, descripcion: cc.descripcion, activo: cc.activo });
    setDlgOpen(true);
  }

  async function guardar() {
    if (!form.codigo.trim() || !form.descripcion.trim()) { setError('Código y descripción son requeridos'); return; }
    setSaving(true);
    try {
      if (dlgEdit) {
        await axios.put(`${API}/centros-costo/${dlgEdit.id}`, form);
        setSuccess('Centro de costo actualizado');
      } else {
        await axios.post(`${API}/centros-costo`, { ...form, empresa_id: empresaId });
        setSuccess('Centro de costo creado');
      }
      setDlgOpen(false);
      cargarCentros();
    } catch (e) { setError(e.response?.data?.error || 'Error al guardar'); }
    setSaving(false);
  }

  async function eliminar(cc) {
    if (!window.confirm(`¿Eliminar "${cc.codigo} - ${cc.descripcion}"?`)) return;
    try {
      await axios.delete(`${API}/centros-costo/${cc.id}`);
      setSuccess('Eliminado correctamente');
      cargarCentros();
    } catch (e) { setError(e.response?.data?.error || 'Error al eliminar'); }
  }

  async function generarReporte() {
    if (!empresaId || !periodoDesde || !periodoHasta) { setError('Selecciona empresa y rango de períodos'); return; }
    setLoadingReporte(true);
    setError('');
    try {
      const params = new URLSearchParams({ empresa_id: empresaId, periodo_desde: periodoDesde, periodo_hasta: periodoHasta });
      if (ccFiltro) params.append('centro_costo_id', ccFiltro);
      const r = await axios.get(`${API}/centros-costo/reporte?${params}`);
      setReporte(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error al generar reporte'); }
    setLoadingReporte(false);
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <AccountTreeIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">Centros de Costo</Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Administrar Centros" />
        <Tab label="Reporte P&L por Centro" icon={<BarChartIcon />} iconPosition="start" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* ── TAB 0: CRUD ── */}
      {tab === 0 && (
        <Box>
          <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm="auto">
                <TextField select fullWidth size="small" label="Empresa" value={empresaId} onChange={e => setEmpresaId(e.target.value)} sx={{ minWidth: '24em' }}>
                  {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNuevo} disabled={!empresaId}>Nuevo Centro</Button>
                  <IconButton onClick={cargarCentros} disabled={!empresaId || loading}><RefreshIcon /></IconButton>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} /> : (
            <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Código</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Estado</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Movimientos</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {centros.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay centros de costo registrados</TableCell></TableRow>
                  ) : centros.map(cc => (
                    <TableRow key={cc.id} hover>
                      <TableCell sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{cc.codigo}</TableCell>
                      <TableCell>{cc.descripcion}</TableCell>
                      <TableCell align="center">
                        <Chip label={cc.activo ? 'Activo' : 'Inactivo'} color={cc.activo ? 'success' : 'default'} size="small" />
                      </TableCell>
                      <TableCell align="center">{cc.total_movimientos}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEditar(cc)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Eliminar"><span><IconButton size="small" color="error" onClick={() => eliminar(cc)} disabled={Number(cc.total_movimientos) > 0}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ── TAB 1: REPORTE P&L ── */}
      {tab === 1 && (
        <Box>
          <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm="auto">
                <TextField select fullWidth size="small" label="Empresa" value={empresaId} onChange={e => setEmpresaId(e.target.value)} sx={{ minWidth: '24em' }}>
                  {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm="auto">
                <TextField select fullWidth size="small" label="Período Desde" value={periodoDesde} onChange={e => setPeriodoDesde(e.target.value)} disabled={!empresaId} sx={{ minWidth: '16em' }}>
                  {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{p.nombre || p.periodo}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm="auto">
                <TextField select fullWidth size="small" label="Período Hasta" value={periodoHasta} onChange={e => setPeriodoHasta(e.target.value)} disabled={!empresaId} sx={{ minWidth: '16em' }}>
                  {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{p.nombre || p.periodo}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm="auto">
                <TextField select fullWidth size="small" label="Centro de Costo (todos)" value={ccFiltro} onChange={e => setCcFiltro(e.target.value)} disabled={!empresaId} sx={{ minWidth: '20em' }}>
                  <MenuItem value="">Todos los centros</MenuItem>
                  {centros.filter(c => c.activo).map(c => <MenuItem key={c.id} value={c.id}>{c.codigo} - {c.descripcion}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Button variant="contained" fullWidth startIcon={<BarChartIcon />} onClick={generarReporte} disabled={!empresaId || loadingReporte}>
                  {loadingReporte ? <CircularProgress size={20} color="inherit" /> : 'Generar'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {reporte && (
            <>
              {reporte.centros.length === 0 ? (
                <Alert severity="info">No se encontraron movimientos con centro de costo para el período seleccionado.</Alert>
              ) : reporte.centros.map(cc => (
                <Paper key={cc.cc_id} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">{cc.cc_codigo} — {cc.cc_nombre}</Typography>
                      <Typography variant="body2" color="text.secondary">Período: {reporte.periodo_desde} → {reporte.periodo_hasta}</Typography>
                    </Box>
                    <Chip
                      label={cc.resultado >= 0 ? `UTILIDAD S/ ${fmt(cc.resultado)}` : `PÉRDIDA S/ ${fmt(Math.abs(cc.resultado))}`}
                      color={cc.resultado >= 0 ? 'success' : 'error'}
                      sx={{ fontWeight: 'bold', fontSize: '1rem', px: 2, py: 1, height: 'auto' }}
                    />
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                        <Typography variant="caption" color="success.dark" fontWeight="bold">INGRESOS</Typography>
                        <Typography variant="h6" color="success.dark" fontWeight="bold">S/ {fmt(cc.total_ingresos)}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: '#fce4ec' }}>
                        <Typography variant="caption" color="error.dark" fontWeight="bold">GASTOS</Typography>
                        <Typography variant="h6" color="error.dark" fontWeight="bold">S/ {fmt(cc.total_gastos + cc.total_costos)}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: cc.resultado >= 0 ? '#e3f2fd' : '#fff3e0' }}>
                        <Typography variant="caption" color={cc.resultado >= 0 ? 'primary.dark' : 'warning.dark'} fontWeight="bold">RESULTADO</Typography>
                        <Typography variant="h6" color={cc.resultado >= 0 ? 'primary.dark' : 'warning.dark'} fontWeight="bold">S/ {fmt(cc.resultado)}</Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Divider sx={{ mb: 1 }} />
                  <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cuenta</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell align="right">Debe</TableCell>
                        <TableCell align="right">Haber</TableCell>
                        <TableCell align="right">Saldo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cc.cuentas.map((c, i) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{c.cuenta_codigo}</TableCell>
                          <TableCell>{c.cuenta_nombre}</TableCell>
                          <TableCell><Chip label={c.tipo_cuenta} size="small" variant="outlined" /></TableCell>
                          <TableCell align="right">{fmt(c.total_debe)}</TableCell>
                          <TableCell align="right">{fmt(c.total_haber)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: c.tipo_cuenta === 'INGRESO' ? 'success.main' : 'error.main' }}>
                            {fmt(Math.abs(c.saldo))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </Box>
                </Paper>
              ))}
            </>
          )}
        </Box>
      )}

      {/* Dialog CRUD */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{dlgEdit ? 'Editar Centro de Costo' : 'Nuevo Centro de Costo'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Código" value={form.codigo}
                onChange={e => setForm(f => ({ ...f, codigo: e.target.value.toUpperCase() }))}
                placeholder="CC-01" inputProps={{ maxLength: 20 }} />
            </Grid>
            <Grid item xs={8}>
              <TextField fullWidth size="small" label="Descripción" value={form.descripcion}
                onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                placeholder="Ej: Administración, Ventas, Producción" inputProps={{ maxLength: 150 }} />
            </Grid>
            {dlgEdit && (
              <Grid item xs={12}>
                <TextField select fullWidth size="small" label="Estado" value={form.activo} onChange={e => setForm(f => ({ ...f, activo: e.target.value }))}>
                  <MenuItem value={1}>Activo</MenuItem>
                  <MenuItem value={0}>Inactivo</MenuItem>
                </TextField>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (dlgEdit ? 'Actualizar' : 'Crear')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

